import React, { createContext, useContext, useMemo, ReactNode } from "react"
import { useDataConnectionMapping, DeviceSource, MappedAsset, SuggestedMapping, BatchMapResult } from "../hooks/useDataConnectionMapping"
import { useSystemViewData } from "./helpers/useSystem"

// ─── System Assets ────────────────────────────────────────────────────────────

interface SystemAsset {
  id: string
  name: string
  assetClass: string | null
}

function useSystemAssetsInternal(): SystemAsset[] {
  const viewData = useSystemViewData()

  return useMemo(() => {
    if (!viewData) return []

    const assets: SystemAsset[] = []
    const seen = new Set<string>()
    const parentAssetIds = new Set<string>()

    // First pass: identify parent asset IDs (nodes that have child assets)
    const collectParentIds = (node: { asset?: { id: string } | null; childAssets?: Array<unknown> }) => {
      if (node.asset && node.childAssets && node.childAssets.length > 0) {
        parentAssetIds.add(node.asset.id)
      }
    }

    for (const group of viewData.installations ?? []) {
      for (const node of group.nodes ?? []) {
        collectParentIds(node)
      }
    }
    for (const node of viewData.isolatedNodes ?? []) {
      collectParentIds(node)
    }

    // Second pass: collect only non-parent assets
    const addAsset = (node: { asset?: { id: string; name: string; class?: string; kind?: string } | null }) => {
      if (node.asset && !seen.has(node.asset.id) && !parentAssetIds.has(node.asset.id)) {
        seen.add(node.asset.id)
        assets.push({
          id: node.asset.id,
          name: node.asset.name,
          assetClass: node.asset.class ?? null,
        })
      }
    }

    const addChildAssets = (node: { childAssets?: Array<{ id: string; name: string; assetClass: string | null }> }) => {
      if (node.childAssets) {
        for (const child of node.childAssets) {
          if (!seen.has(child.id)) {
            seen.add(child.id)
            assets.push({
              id: child.id,
              name: child.name,
              assetClass: child.assetClass ?? null,
            })
          }
        }
      }
    }

    for (const group of viewData.installations ?? []) {
      for (const node of group.nodes ?? []) {
        addAsset(node)
        addChildAssets(node)
      }
    }

    for (const node of viewData.isolatedNodes ?? []) {
      addAsset(node)
      addChildAssets(node)
    }

    return assets.sort((a, b) => a.name.localeCompare(b.name))
  }, [viewData])
}

// ─── Context Interfaces ───────────────────────────────────────────────────────

/** Stable data that changes infrequently (sources, assets, suggestions) */
interface MapAssetDataState {
  sources: DeviceSource[]
  mappedAssets: MappedAsset[]
  suggestedMappings: SuggestedMapping[]
  systemAssets: SystemAsset[]
  loadingSources: boolean
  loadingMapped: boolean
  loadingSuggestions: boolean
}

/** Mutable/async actions */
interface MapAssetDataActions {
  refreshSources: () => Promise<boolean>
  mapAssetAndRefetch: (assetId: string, sourceRef: string) => Promise<void>
  batchMapAssets: (mappings: Array<{ assetId: string; sourceRef: string }>) => Promise<BatchMapResult>
}

/** Volatile state that changes during async operations */
interface MapAssetDataStatus {
  refreshing: boolean
  mapping: boolean
  supportsRefresh: boolean
  discoveredAt: string | null
  connectorType: string
  stale: boolean
}

// ─── Contexts (split to minimise re-renders) ──────────────────────────────────

const MapAssetDataStateContext = createContext<MapAssetDataState | undefined>(undefined)
const MapAssetDataActionsContext = createContext<MapAssetDataActions | undefined>(undefined)
const MapAssetDataStatusContext = createContext<MapAssetDataStatus | undefined>(undefined)

// ─── Provider ─────────────────────────────────────────────────────────────────

interface MapAssetDataProviderProps {
  monitoringInstanceId: string
  excludeAssetIds?: Set<string>
  children: ReactNode
}

export const MapAssetDataProvider: React.FC<MapAssetDataProviderProps> = ({ monitoringInstanceId, excludeAssetIds, children }) => {
  const allSystemAssets = useSystemAssetsInternal()
  const systemAssetIds = useMemo(() => allSystemAssets.map((a) => a.id), [allSystemAssets])
  const {
    sources,
    loadingSources,
    mappedAssets,
    loadingMapped,
    mapAssetAndRefetch,
    batchMapAssets,
    mapping,
    suggestedMappings,
    loadingSuggestions,
    supportsRefresh,
    discoveredAt,
    connectorType,
    stale,
    refreshSources,
    refreshing,
  } = useDataConnectionMapping(monitoringInstanceId, systemAssetIds)

  // Filter out assets that are mapped in sibling connections
  const systemAssets = useMemo(() => {
    if (!excludeAssetIds || excludeAssetIds.size === 0) return allSystemAssets
    return allSystemAssets.filter((a) => !excludeAssetIds.has(a.id))
  }, [allSystemAssets, excludeAssetIds])

  const state = useMemo<MapAssetDataState>(() => ({
    sources,
    mappedAssets,
    suggestedMappings,
    systemAssets,
    loadingSources,
    loadingMapped,
    loadingSuggestions,
  }), [sources, mappedAssets, suggestedMappings, systemAssets, loadingSources, loadingMapped, loadingSuggestions])

  const actions = useMemo<MapAssetDataActions>(() => ({
    refreshSources,
    mapAssetAndRefetch,
    batchMapAssets,
  }), [refreshSources, mapAssetAndRefetch, batchMapAssets])

  const status = useMemo<MapAssetDataStatus>(() => ({
    refreshing,
    mapping,
    supportsRefresh,
    discoveredAt,
    connectorType,
    stale,
  }), [refreshing, mapping, supportsRefresh, discoveredAt, connectorType, stale])

  return (
    <MapAssetDataStateContext.Provider value={state}>
      <MapAssetDataActionsContext.Provider value={actions}>
        <MapAssetDataStatusContext.Provider value={status}>
          {children}
        </MapAssetDataStatusContext.Provider>
      </MapAssetDataActionsContext.Provider>
    </MapAssetDataStateContext.Provider>
  )
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useMapAssetDataState(): MapAssetDataState {
  const ctx = useContext(MapAssetDataStateContext)
  if (!ctx) throw new Error("useMapAssetDataState must be used within MapAssetDataProvider")
  return ctx
}

export function useMapAssetDataActions(): MapAssetDataActions {
  const ctx = useContext(MapAssetDataActionsContext)
  if (!ctx) throw new Error("useMapAssetDataActions must be used within MapAssetDataProvider")
  return ctx
}

export function useMapAssetDataStatus(): MapAssetDataStatus {
  const ctx = useContext(MapAssetDataStatusContext)
  if (!ctx) throw new Error("useMapAssetDataStatus must be used within MapAssetDataProvider")
  return ctx
}

export type { SystemAsset }
